import React from 'react';
import { ArrowLeft, Archive } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import type { Project } from '../types/core';
import ProjectCard from './ProjectCard';
import { isOverdue, isDeadlineApproaching } from '../utils';

interface ArchivedProjectsProps {
  projects: Project[];
  onViewProject: (project: Project) => void;
  onDeleteProject: (project: Project) => void;
  onBack: () => void;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}

const ArchivedProjects: React.FC<ArchivedProjectsProps> = ({ projects, onViewProject, onDeleteProject, onBack, setProjects }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  if (!currentUser) return null;

  const archivedProjects = projects.filter(p => p.status === 'closed');

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors">
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold flex items-center">
            <Archive className="w-6 h-6 mr-2 text-gray-600" />
            {t('project.archived', { defaultValue: 'Archived Projects' })} ({archivedProjects.length})
          </h1>
        </div>
      </div>
      {archivedProjects.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow">
          <Archive className="w-20 h-20 mx-auto text-gray-300" />
          <p className="mt-4 text-xl">{t('project.no.archived', { defaultValue: 'No archived projects' })}</p>
        </div>
      ) : (
        <div className="project-card-list-grid">
          {archivedProjects.map((p: Project) => (
            <ProjectCard 
              key={p.id} 
              project={p} 
              onView={onViewProject} 
              onDelete={onDeleteProject} 
              currentUser={currentUser} 
              t={t} 
              isOverdue={isOverdue} 
              isDeadlineApproaching={isDeadlineApproaching}
              setProjects={setProjects}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArchivedProjects;
